// Wave 3 — Public Request-a-Quote form (creates real lead in CRM via divert-safe endpoint).
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell from "../../components/public/PublicShell";
import { api } from "../../lib/api";
import PublicIntake from "../../design-system/intake/PublicIntake";

const TIMELINES = ["ASAP", "1-3 months", "3-6 months", "6-12 months", "Exploring"];
const STATES = ["NJ", "PA", "DE", "MD", "VA", "NC", "SC", "GA", "FL", "OTHER"];

// R1e — verbatim SMS opt-in disclosure text. Any change here MUST bump the
// backend `terms_version` / `policy_version` constants so consent evidence
// remains auditable at Twilio Trust Hub review time.
export const SMS_CONSENT_DISCLOSURE_TEXT =
  "I agree to receive SMS messages from ERF ENTERPRISES LLC regarding my inquiries, estimates, projects, appointments, services, account activity, and related operational communications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. View our Privacy Policy and Terms & Conditions.";

export default function QuotePublic() {
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service_slug: params.get("service") || "",
    project_state: "", project_city: "",
    timeline: "", description: "", honeypot: "",
    // R1e — SMS opt-in. Never preselected. Never bundled with T&C acceptance.
    sms_consent_granted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const refParam = params.get("ref") || "";

  useEffect(() => {
    api.get("/public/website/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const upd = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!form.name || !form.email || !form.description) {
      setError("Name, email, and project description are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service_slug: form.service_slug || null,
        project_state: form.project_state || null,
        project_city: form.project_city || null,
        timeline: form.timeline || null,
        description: form.description,
        honeypot: form.honeypot || null,
        ref: refParam || null,
        // R1e — only forward the affirmative opt-in state + exact disclosure
        // string shown at consent time. Server records the consent row only
        // when granted === true AND a phone number is provided.
        sms_consent_granted: !!form.sms_consent_granted,
        sms_consent_disclosure_text: form.sms_consent_granted ? SMS_CONSENT_DISCLOSURE_TEXT : null,
        // 3h — pass any files the applicant attached through the public
        // intake control. Backend links them to the resulting lead.
        attachment_ids: attachmentIds,
      };
      const r = await api.post("/public/website/quote", body);
      setSuccess(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (detail?.message) setError(detail.message);
      else setError("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  const hero = (
    <section className="erf-public-hero" style={{ padding: "72px 0 56px" }} data-testid="quote-hero">
      <div className="erf-public-container">
        <div className="erf-public-hero-eyebrow">Request a Quote</div>
        <h1 style={{ maxWidth: 800 }}>Tell us about your project.</h1>
        <p className="lead">One business day response. Every submission goes straight to Edwin.</p>
      </div>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <section className="erf-public-section">
        <div className="erf-public-container" style={{ maxWidth: 780 }}>
          {success ? (
            <div className="erf-public-empty" data-testid="quote-success" style={{ borderStyle: "solid", background: "var(--exec-bg-alt)" }}>
              <h3>Thank you.</h3>
              <p>{success.message || "We'll be in touch."}</p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
                <Link to="/" className="erf-public-cta-outline">Back to home</Link>
                <Link to="/services" className="erf-public-cta">See our services</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="erf-public-form" data-testid="quote-form">
              <div className="erf-public-form-row-2">
                <div>
                  <label>Your name</label>
                  <input type="text" value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="quote-name" />
                </div>
                <div>
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="quote-email" />
                </div>
              </div>
              <div className="erf-public-form-row-2">
                <div>
                  <label>Phone (optional)</label>
                  <input type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="quote-phone" />
                </div>
                <div>
                  <label>Service interest</label>
                  <select value={form.service_slug} onChange={(e) => upd("service_slug", e.target.value)} data-testid="quote-service">
                    <option value="">— Select —</option>
                    {services.map((s) => (<option key={s.slug} value={s.slug}>{s.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="erf-public-form-row-2">
                <div>
                  <label>Project city</label>
                  <input type="text" value={form.project_city} onChange={(e) => upd("project_city", e.target.value)} data-testid="quote-city" />
                </div>
                <div>
                  <label>Project state</label>
                  <select value={form.project_state} onChange={(e) => upd("project_state", e.target.value)} data-testid="quote-state">
                    <option value="">— Select —</option>
                    {STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label>Timeline</label>
                <select value={form.timeline} onChange={(e) => upd("timeline", e.target.value)} data-testid="quote-timeline">
                  <option value="">— Select —</option>
                  {TIMELINES.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </div>
              <div>
                <label>Project description</label>
                <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} required data-testid="quote-description" placeholder="What are you looking to build, remodel, or restore?" />
              </div>
              <PublicIntake formSlug="qa-quote-form" onIdsChange={setAttachmentIds} />
              <div className="erf-public-honeypot" aria-hidden="true">
                <label htmlFor="hp">Leave blank</label>
                <input id="hp" type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(e) => upd("honeypot", e.target.value)} />
              </div>

              {/* R1e — A2P 10DLC opt-in. SEPARATE from T&C acceptance.
                   Optional; form submits fine either way. Never preselected. */}
              <div
                className="erf-public-form-consent"
                data-testid="sms-consent-block"
                style={{
                  marginTop: 8, paddingTop: 16,
                  borderTop: "1px solid var(--exec-border)",
                }}
              >
                <label
                  htmlFor="quote-sms-consent"
                  style={{
                    display: "flex", gap: 12, alignItems: "flex-start",
                    cursor: "pointer", fontSize: 13, lineHeight: 1.55,
                    color: "var(--exec-body)",
                  }}
                >
                  <input
                    id="quote-sms-consent"
                    name="sms_consent_granted"
                    type="checkbox"
                    defaultChecked={false}
                    checked={form.sms_consent_granted}
                    onChange={(e) => upd("sms_consent_granted", e.target.checked)}
                    data-testid="quote-sms-consent-checkbox"
                    style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, cursor: "pointer" }}
                  />
                  <span data-testid="quote-sms-consent-disclosure">
                    I agree to receive SMS messages from ERF ENTERPRISES LLC regarding my inquiries,
                    estimates, projects, appointments, services, account activity, and related
                    operational communications. Message frequency varies. Message and data rates may
                    apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help.
                    Consent is not a condition of purchase. View our{" "}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="quote-sms-privacy-link">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" data-testid="quote-sms-terms-link">
                      Terms &amp; Conditions
                    </a>
                    .
                  </span>
                </label>
              </div>

              {error && <div style={{ color: "#B91C1C", fontSize: 14, marginTop: 4 }} data-testid="quote-error">{error}</div>}
              <div style={{ marginTop: 8 }}>
                <button type="submit" className="erf-public-cta" disabled={submitting} data-testid="quote-submit" style={{ border: 0, cursor: submitting ? "wait" : "pointer" }}>
                  {submitting ? "Sending…" : "Submit request"}
                </button>
              </div>
              <p style={{ fontSize: 12, color: "var(--exec-body)", marginTop: 8, lineHeight: 1.55 }}>
                Your submission goes straight to Edwin and is stored in our system with an audit-tracked reference number.
                We'll reach out within one business day. We do not share your information.
              </p>
            </form>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
